import type { VoidFunctionComponent } from 'react';

import type { Project } from '../../../../../../shared/projectsV2';
import { nuc } from '../../../../../utils';
import Icon, { icons } from '../../../../generic/Icon';
import Tooltip from '../../../../generic/Tooltip';
import { BadgeColors, BadgeSizes, RouterLink, TooltipBadge } from '../../../../generic';
import { projectTypes } from '../../../shared/projectTypesIconMap';

type Props = {
  project: Project;
};

export type ProjectTypesKey = keyof typeof projectTypes;

export const ProjectIcon: VoidFunctionComponent<{ projectTypeKey: ProjectTypesKey }> = ({ projectTypeKey }) => {
  const projectType = projectTypes[projectTypeKey];

  if (!projectType) {
    return null;
  }

  const title = projectType.title;
  const icon = projectType.icon;

  return (
    <Tooltip tooltip={title} id="project-icon-tooltip">
      <span className="project-item__title_icon">
        <Icon icon={icon} />
      </span>
    </Tooltip>
  );
};

const TooltipContent: VoidFunctionComponent<{ originOrganizationName: string }> = ({ originOrganizationName }) => {
  return (
    <>
      <Icon icon={icons.circleInfo} />
      {' '}Shared by {originOrganizationName}
    </>
  );
};

const MAX_LENGTH = 50;
const OriginOrganizationNameBadge: VoidFunctionComponent<{
  originOrganizationName: string | undefined,
}> = ({ originOrganizationName }) => {
  if (!originOrganizationName) {
    return null;
  }

  let truncatedName = originOrganizationName;
  if (truncatedName.length > MAX_LENGTH) {
    truncatedName = `${truncatedName.substring(0, MAX_LENGTH)}...`;
  }

  return (
    <TooltipBadge
      id="origin-organization-name"
      tooltip={<TooltipContent originOrganizationName={originOrganizationName} />}
      color={BadgeColors.LightGray}
      size={BadgeSizes.Micro}
    >
      {truncatedName}
    </TooltipBadge>
  );
};

const ProjectTitle: VoidFunctionComponent<Props> = ({ project }: Props) => {
  return (
    <div className="project-item__title">
      <RouterLink
        to={`/projects/${nuc(project.id)}`}
        className="projects-table__row__link"
      >
        <Tooltip showTooltip tooltip={project.title} id="project-title-tooltip">
          <div className="project-item__title__text">{project.title}</div>
        </Tooltip>
      </RouterLink>
      {!project.public && <ProjectIcon projectTypeKey="private" />}
      {project.type && <ProjectIcon projectTypeKey={project.type as ProjectTypesKey} />}
      <OriginOrganizationNameBadge originOrganizationName={project.originOrganizationName} />
    </div>
  );
};

export default ProjectTitle;
